import React, { useEffect, useRef, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { removeToken } from '../../services/TokenStorage';
import { doLogout } from '../../services/AuthService';
import { processError } from '../../services/ServiceUtils';
import MarketIndicator from './MarketIndicator';
import { getCurrentMarket, subscribeToCurrentMarket, MARKET_FUTURES } from '../../utils/MarketStorage';
import SideBarItem from './SideBarItem';
import { getEnvironment, getUserName } from '../../services/EnvironmentStorage';
import { getPathHomePage } from '../../utils/HomePageUtils';
import OperationalHealthIndicator from './OperationalHealthIndicator';
import SystemStatusBar from './SystemStatusBar';
import OnboardingGuideModal from '../OnboardingGuide/OnboardingGuideModal';
import ToastHistoryModal from '../Toast/ToastHistoryModal';
import CurrentEnvironmentToggle from './CurrentEnvironmentToggle';
import SessionRenewalBanner from './SessionRenewalBanner';
import CircuitBreakerIndicator from './CircuitBreakerIndicator';

function SideBar() {

    const history = useHistory();

    const [usrName, setUsrName] = useState('');
    const [environment, setEnvironment] = useState('');
    const [showServerEnv, setShowServerEnv] = useState(false);
    const [market, setMarket] = useState(getCurrentMarket());
    useEffect(() => subscribeToCurrentMarket(setMarket), []);
    const isFuturesMarket = market === MARKET_FUTURES;

    function capitalizeFirstLetter(value) {
        if (!value) return value;
        return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
    }

    useEffect(() => {
        const rawEnv = (getEnvironment() || '').toLowerCase();
        setUsrName(getUserName());
        // Em produção, omitir a label do servidor (sem valor, só ruído ao lado do toggle abaixo).
        // Em dev/test, mostrar como aviso de que não estamos rodando contra o backend de produção.
        if (rawEnv && rawEnv !== 'production') {
            setEnvironment(`Servidor: ${capitalizeFirstLetter(rawEnv)}`);
            setShowServerEnv(true);
        }
    }, []);

    const ttIntelligence = useRef(null);
    const ttStrategies = useRef(null);
    const ttCash = useRef(null);
    const ttTemplates = useRef(null);
    const ttSettings = useRef(null);
    const ttGuide = useRef(null);

    useEffect(() => {
        if (!window.bootstrap) return;
        const opts = { animation: false, placement: 'right', trigger: 'hover' };
        const pairs = [
            ['sidebar-tt-intelligence', ttIntelligence, 'Visão geral do desempenho: dashboard, relatórios e performance'],
            ['sidebar-tt-strategies', ttStrategies, 'Suas estratégias e os blocos que as compõem: monitores e automações'],
            ['sidebar-tt-cash', ttCash, 'Seu dinheiro num lugar só: saldo da carteira e saques (modelos, segurança e realização de lucro)'],
            ['sidebar-tt-templates', ttTemplates, 'Templates reutilizáveis para ordens e webhooks'],
            ['sidebar-tt-settings', ttSettings, 'Suas configurações pessoais, corretora, IA e símbolos disponíveis'],
            ['sidebar-tt-guide', ttGuide, 'Guia passo a passo para configurar corretora, IA, monitores e estratégias'],
        ];
        pairs.forEach(([id, ref, title]) => {
            const el = document.getElementById(id);
            if (!el) return;
            ref.current = new window.bootstrap.Tooltip(el, { ...opts, title });
            el.addEventListener('click', () => ref.current && ref.current.hide());
        });
        return () => pairs.forEach(([, ref]) => { if (ref.current) { ref.current.dispose(); ref.current = null; } });
    }, []);

    function clearAndRedirect() {
        removeToken();
        history.push('/');
    }

    function onLogoutClick(event) {
        doLogout()
            .then(response => clearAndRedirect())
            .catch(err => {
                processError(err);
                clearAndRedirect();
            })
    }

    return (
        <React.Fragment>
            <nav id="sidebarMenu" className="sidebar d-lg-block bg-gray-800 text-white collapse" datasimplebar="true">
                <div className="sidebar-inner px-3 pt-3" style={{ overflowX: 'hidden' }}>
                    <div className="user-card d-flex d-md-none align-items-center justify-content-between justify-content-md-center pb-4">
                        <div className="collapse-close d-md-none">
                            <a href="#sidebarMenu" data-bs-toggle="collapse"
                                data-bs-target="#sidebarMenu" aria-controls="sidebarMenu" aria-expanded="true"
                                aria-label="Toggle navigation">
                                <svg className="icon icon-xs" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"></path></svg>
                            </a>
                        </div>
                    </div>
                    <ul className="nav flex-column pt-3 pt-md-0">

                        {/* Ordem do topo: software → usuário → mercado → corretora+ambiente */}
                        <li className="nav-item mb-2">
                            <Link to={getPathHomePage()} className="nav-link d-flex align-items-center justify-content-center">
                                <span className="sidebar-icon">
                                    <img src="/img/favicon/favicon-32x32.png" height="32" width="32" alt="Beholder Logo" />
                                </span>
                                <span className="mt-1 ms-1 sidebar-text">Beholder 2.0</span>
                            </Link>
                        </li>

                        <li className="nav-item mb-3 text-center">
                            <span className="mt-1 ms-1 sidebar-text text-success">{usrName}</span>
                        </li>
                        {showServerEnv && (
                            <li className="nav-item mb-2 text-center">
                                <span className="mt-1 ms-1 sidebar-text text-warning" title="Você está conectado a um backend que não é o de produção">
                                    {environment}
                                </span>
                            </li>
                        )}
                        {/* Circuit breaker disparado: faixa vermelha PERSISTENTE no topo (impossível de
                            ignorar). Só renderiza quando ativo; some sozinha ao resetar. */}
                        <CircuitBreakerIndicator />

                        {/* Seletor de MERCADO (abaixo do usuário), acima de corretora/ambiente — mercado é a lente acima de prod/teste. */}
                        <MarketIndicator />
                        <CurrentEnvironmentToggle />

                        {/* Inteligência: visível nos 2 mercados — as 4 telas (Dashboard/Relatórios/
                            Performance/Carteira) são cientes do mercado atual (getCurrentMarket).
                            Em Futuros mostram margem/posições/realizado dos shorts; em Spot, o de sempre. */}
                        <li className='nav-item'>
                            <span
                                id='sidebar-tt-intelligence'
                                className='nav-link collapsed d-flex justify-content-between align-items-center'
                                data-bs-toggle='collapse'
                                data-bs-target='#submenu-intelligence'
                            >
                                <span>
                                    <span className='sidebar-icon'>
                                        <svg className="icon icon-xs me-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path d="M2 10a8 8 0 018-8v8h8a8 8 0 11-16 0z"></path><path d="M12 2.252A8.014 8.014 0 0117.748 8H12V2.252z"></path></svg>
                                    </span>
                                    <span className='sidebar-text'>Inteligência</span>
                                </span>
                                <span className='link-arrow'>
                                    <svg className="icon icon-sm" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd"></path>
                                    </svg>
                                </span>
                            </span>
                            <div className='multi-level collapse' role='list' id='submenu-intelligence'>
                                <ul className='flex-column nav'>
                                    <SideBarItem to="/dashboard" text="Dashboard"
                                        tooltip="Visão geral em tempo real: saldo, posições abertas, P&L do dia e indicadores da IA" />
                                    <SideBarItem to="/performance" text="Performance"
                                        tooltip="Análise de desempenho das estratégias: taxa de acerto, lucro médio e drawdown" />
                                    <SideBarItem to="/reports" text="Relatórios"
                                        tooltip="Histórico de ordens executadas com filtros de data, símbolo e resultado" />
                                    <SideBarItem to="/tax" text="Imposto de Renda"
                                        tooltip="Apuração de ganho de capital sobre cripto: resumo mensal (GCAP/DARF 4600), posição anual e arquivo IN 1.888" />
                                </ul>
                            </div>
                        </li>

                        <li className='nav-item'>
                            <span
                                id='sidebar-tt-strategies'
                                className='nav-link collapsed d-flex justify-content-between align-items-center'
                                data-bs-toggle='collapse'
                                data-bs-target='#submenu-strategies'
                            >
                                <span>
                                    <span className='sidebar-icon'>
                                        <svg className="icon icon-xs me-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path d="M10 3.5a1.5 1.5 0 013 0V4a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-.5a1.5 1.5 0 000 3h.5a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-.5a1.5 1.5 0 00-3 0v.5a1 1 0 01-1 1H6a1 1 0 01-1-1v-3a1 1 0 00-1-1h-.5a1.5 1.5 0 010-3H4a1 1 0 001-1V6a1 1 0 011-1h3a1 1 0 001-1v-.5z" /></svg>
                                    </span>
                                    <span className='sidebar-text'>Estratégias</span>
                                </span>
                                <span className='link-arrow'>
                                    <svg className="icon icon-sm" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd"></path>
                                    </svg>
                                </span>
                            </span>
                            <div className='multi-level collapse' role='list' id='submenu-strategies'>
                                <ul className='flex-column nav'>
                                    <SideBarItem to="/strategies" text="Estratégias"
                                        tooltip="Agrupe monitor + automações em uma estratégia ativável com um clique. Use os Templates para começar rápido" />
                                    <SideBarItem to="/automations" text="Automações"
                                        tooltip="Regras automáticas: define condições de mercado e ações a executar (ordens, alertas, etc.)" />
                                    <SideBarItem to="/monitors" text="Monitores"
                                        tooltip="Coleta dados de mercado em tempo real (candles, regime, scanner). Necessário para as automações funcionarem" />
                                    {/* WebHook é um gatilho de entrada que dispara automações — vive
                                        junto da lógica de Estratégias, não com os templates de ordem. */}
                                    <SideBarItem to="/webhooks" text="WebHook"
                                        tooltip="Receba sinais externos (TradingView, bots) e dispare automações via URL" />
                                    <SideBarItem to="/launchpad" text="Lançamentos"
                                        tooltip="Radar de moedas recém-listadas na Binance + preparar uma estratégia pausada pra estreia (alto risco)" />
                                </ul>
                            </div>
                        </li>

                        {/* Ordens: a lista de execuções (spot) e os templates de ordem reutilizáveis
                            num grupo só. Futuros tem sua própria tela contextual abaixo. */}
                        <li className='nav-item'>
                            <span
                                id='sidebar-tt-orders'
                                className='nav-link collapsed d-flex justify-content-between align-items-center'
                                data-bs-toggle='collapse'
                                data-bs-target='#submenu-orders'
                            >
                                <span>
                                    <span className='sidebar-icon'>
                                        <svg className="icon icon-xs me-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z"></path><path fillRule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clipRule="evenodd"></path></svg>
                                    </span>
                                    <span className='sidebar-text'>Ordens</span>
                                </span>
                                <span className='link-arrow'>
                                    <svg className="icon icon-sm" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd"></path>
                                    </svg>
                                </span>
                            </span>
                            <div className='multi-level collapse' role='list' id='submenu-orders'>
                                <ul className='flex-column nav'>
                                    {/* A lista de ordens é contextual ao mercado: spot mostra
                                        Ordens; futuros mostra Futuros (posições alavancadas). */}
                                    {!isFuturesMarket && (
                                        <SideBarItem to="/orders" text="Ordens"
                                            tooltip="Visualize e execute ordens de compra e venda manualmente ou acompanhe as automáticas" />
                                    )}
                                    {isFuturesMarket && (
                                        <SideBarItem to="/futures" text="Futuros"
                                            tooltip="Opere contratos de futuros (derivativos USDT-M) com alavancagem — long e short" />
                                    )}
                                    <SideBarItem to="/orderTemplates" text="Templates de Ordem"
                                        tooltip="Templates de ordem reutilizáveis pelas automações (tipo, lado, quantidade)" />
                                </ul>
                            </div>
                        </li>

                        {/* Caixa: o fluxo de dinheiro num lugar só — ver o saldo (Carteira) e tirar
                            dinheiro (Saques: modelos + segurança + realização de lucro). */}
                        <li className='nav-item'>
                            <span
                                id='sidebar-tt-cash'
                                className='nav-link collapsed d-flex justify-content-between align-items-center'
                                data-bs-toggle='collapse'
                                data-bs-target='#submenu-cash'
                            >
                                <span>
                                    <span className='sidebar-icon'>
                                        <svg className="icon icon-xs me-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" /><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.511 1.31c.562.649 1.413 1.076 2.354 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.414-1.076-2.354-1.253V5z" clipRule="evenodd" /></svg>
                                    </span>
                                    <span className='sidebar-text'>Caixa</span>
                                </span>
                                <span className='link-arrow'>
                                    <svg className="icon icon-sm" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd"></path>
                                    </svg>
                                </span>
                            </span>
                            <div className='multi-level collapse' role='list' id='submenu-cash'>
                                <ul className='flex-column nav'>
                                    <SideBarItem to="/wallet" text="Carteira"
                                        tooltip="Saldo disponível e alocado por ativo na sua corretora" />
                                    {/* Caixa é nível de CONTA (não específico de spot) — Saques e
                                        Histórico aparecem nos 2 mercados, igual à Carteira. */}
                                    <SideBarItem to="/withdrawTemplates" text="Saques"
                                        tooltip="Saques automáticos: modelos, segurança (whitelist/tetos) e realização de lucro" />
                                    <SideBarItem to="/withdrawHistory" text="Histórico de Saques"
                                        tooltip="Auditoria de todos os saques e análise do lucro colhido ao longo do tempo" />
                                </ul>
                            </div>
                        </li>

                        <li className='nav-item'>
                            <span
                                id='sidebar-tt-settings'
                                className='nav-link collapsed d-flex justify-content-between align-items-center'
                                data-bs-toggle='collapse'
                                data-bs-target='#submenu-settings'
                            >
                                <span>
                                    <span className='sidebar-icon'>
                                        <svg className="icon icon-xs me-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd"></path></svg>
                                    </span>
                                    <span className='sidebar-text'>Configurações</span>
                                </span>
                                <span className='link-arrow'>
                                    <svg className="icon icon-sm" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd"></path>
                                    </svg>
                                </span>
                            </span>
                            <div className='multi-level collapse' role='list' id='submenu-settings'>
                                <ul className='flex-column nav'>
                                    <SideBarItem to="/settings" text="Geral"
                                        tooltip="Dados pessoais, gestão de risco, corretora, IA e limpeza de dados" />
                                    <SideBarItem to="/symbols" text="Símbolos"
                                        tooltip="Pares de negociação disponíveis na corretora. Adicione favoritos para acompanhá-los no dashboard" />
                                </ul>
                            </div>
                        </li>

                        <li role="separator" className="dropdown-divider mt-4 mb-3 border-gray-700"></li>

                        <li className='nav-item'>
                            <button
                                className='nav-link w-100 text-start border-0 bg-transparent d-flex align-items-center'
                                data-bs-toggle='modal'
                                data-bs-target='#modalToastHistory'
                            >
                                <span className='sidebar-icon'>
                                    <svg className='icon icon-xs me-2' fill='none' stroke='currentColor' viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg'>
                                        <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9' />
                                    </svg>
                                </span>
                                <span className='sidebar-text'>Notificações</span>
                            </button>
                        </li>

                        <li className='nav-item'>
                            <button
                                className='nav-link w-100 text-start border-0 bg-transparent d-flex align-items-center'
                                id='sidebar-tt-guide'
                                data-bs-toggle='modal'
                                data-bs-target='#modalGuide'
                            >
                                <span className='sidebar-icon'>
                                    <svg className='icon icon-xs me-2' fill='none' stroke='currentColor' viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg'>
                                        <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z' />
                                    </svg>
                                </span>
                                <span className='sidebar-text'>Ajuda / Guia</span>
                            </button>
                        </li>

                        <SideBarItem to="/" text="Sair" onClick={onLogoutClick}
                            tooltip="Encerrar sessão e voltar para a tela de login">
                            <svg className="icon icon-xs me-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"></path></svg>
                        </SideBarItem>
                        <SessionRenewalBanner />
                        <OperationalHealthIndicator />
                        <SystemStatusBar />
                    </ul>
                </div>
            </nav>
            <OnboardingGuideModal />
            <ToastHistoryModal />
        </React.Fragment>
    );
}

export default SideBar;
